import type { BaseTranslation } from "../i18n-types.js"

const en = {
  common: {
    countViews: "{{no views|1 view|?? views}}",
    countReactions: "{{no reactions|1 reaction|?? reactions}}",
    back: "Back",
    blog: "Posts",
    settings: "Settings",
    addPosts: "Create post",
    save: "Save",
    filter: "Filter",
    tag: "Tag",
    sort: "Sort",
    all: "All",
    loading: "Loading...",
    saving: "Saving...",
    delete: "Delete",
    edit: "Edit",
    close: "Close",
    error: "Error saving data",
    networkError: "Error saving data. Please check your connection",
    validationError: "Please review the form for errors",
    by: "by",
    cancel: "Cancel",
    private: "Private",
    holdToConfirm: "Hold to confirm",
    done: "Done!",
    defaultLanguage: "default",
    translating: "Translating...",
    translateAll: "Translate all",
    translateFrom: "Translate from {language}",
  },
  mainLayout: {
    errorLoadingData: "There was an error loading data for this page. Please try again.",
    reloadPage: "Reload page",
  },
  dashboard: {
    welcomeBack: "Welcome back",
    latestPosts: "Latest posts",
    viewAllPosts: "View all posts",
    noPostsYet: "You haven't published any posts yet.",
    latestDrafts: "Latest drafts",
    viewAllDrafts: "View all",
    noDrafts: "You don't have any drafts",
    latestNotes: "Latest notes",
    noNotesYet: "You don't have any notes yet",
    subscribers: "Subscribers",
    posts: "Posts",
    views: "Views",
    openRate: "Open rate",
    published: "Published",
    created: "Created",
    recentReactions: "Recent reactions",
    noReactionsYet: "You don't have any reactions yet",
    reactedToYourPost: "reacted to your post",
    othersCount: "{{no others|1 other|?? others}}",
    createPublication: {
      title: "Create your own publication",
      description: "Start your own corner on the web. share your thoughts, build your audience, and own your content.",
      button: "Get started",
    },
    verifyEmail: {
      title: "Verify your email",
      description: "Verify your email address to unlock all features, like creating your own publication.",
      button: "Verify email",
    },
  },
  settings: {
    title: "Settings",
    localizationTitle: "Localization",
    localizationDescription: "Set your preferred language",
    interfaceLanguageLabel: "Interface language",
    authenticationTitle: "Authentication",
    authenticationDescription: "Manage your account",
    signedInAs: "Signed in as {email}",
    signOut: "Sign out",
    signingOut: "Signing out...",
    authorProfileTitle: "Author profile",
    authorProfileDescription: "Information about you that will be shown on your publication",
    profileImageLabel: "Profile picture (120×120)",
    authorNameLabel: "Author name",
    authorNamePlaceholder: "Your name",
    bioLabel: "Bio",
    bioPlaceholder: "Tell your readers about yourself",
    imageSaved: "Image uploaded",
    imageDeleted: "Image deleted",
    informationSaved: "Saved successfully",
    social: {
      title: "Social links",
      description: "Connect your social profiles to your publication",
      social_x: {
        label: "X (Twitter)",
        placeholder: "https://x.com/username",
      },
      social_instagram: {
        label: "Instagram",
        placeholder: "https://instagram.com/username",
      },
      social_tiktok: {
        label: "TikTok",
        placeholder: "https://tiktok.com/@username",
      },
      social_youtube: {
        label: "YouTube",
        placeholder: "https://youtube.com/@username",
      },
      social_facebook: {
        label: "Facebook",
        placeholder: "https://facebook.com/username",
      },
      social_linkedin: {
        label: "LinkedIn",
        placeholder: "https://linkedin.com/in/username",
      },
      social_bluesky: {
        label: "Bluesky",
        placeholder: "https://bsky.app/profile/username",
      },
      social_books: {
        label: "Books",
        placeholder: "https://goodreads.com/user/show/username",
      },
      social_website: {
        label: "Website",
        placeholder: "https://yourwebsite.com",
      },
      social_soundcloud: {
        label: "SoundCloud",
        placeholder: "https://soundcloud.com/username",
      },
      social_bandcamp: {
        label: "Bandcamp",
        placeholder: "https://username.bandcamp.com",
      },
    },
    subscriptions: {
      empty: "No subscriptions yet",
      unsubscribeTitle: "Unsubscribe",
      unsubscribeDescription: "You will no longer receive updates from this publication.",
      unsubscribePaidDisclaimer: "Your current paid plan will also be cancelled.",
      holdToUnsubscribe: "Hold to unsubscribe",
      unsubscribing: "Unsubscribing...",
      unsubscribed: "Unsubscribed",
    },
    deleteAccount: {
      title: "Delete account",
      description: "Permanently delete your account and all data",
      warning: "This action cannot be undone. All posts, notes, and subscriber data will be permanently deleted.",
      button: "Delete account",
      confirmTitle: "Delete your account?",
      confirmDescription: "This will permanently delete your account and all content. This cannot be undone.",
      exportBeforeDeletePrefix: "Before deleting, you can",
      exportBeforeDeleteSuffix: "your publication data, including all posts, notes, and subscriber information.",
      typeToConfirm: "Type {text:string} to confirm",
      confirmButton: "Delete my account",
    },
  },
  post: {
    preview: "Preview",
    previewDescription: "See how your post would look for your readers.",
    unarchive: "Unarchive",
    update: "Update",
    updated: "Post updated successfully",
    publish: "Continue",
    uploadHeaderImage: "Upload header images (16:9, 800 × 450 px)",
    title: "Title",
    subtitle: "Subtitle",
    statusSpam: "This version is marked as spam, contact support if you think this is a mistake.",
    statusArchived: "This version is archived, people can't see it, but you can still edit it.",
    statusSent: "This version was sent to your email subscribers",
    statusPublished: "This version is published and visible to your readers",
    statusDraft: "This is a draft version, you can preview it but it's not visible to your readers",
    words: "words",
    savedAt: "Saved at {time}",
    notes: "Notes",
    notesDescription: "Create notes for this post.",
    proofread: "Proofread",
    proofreadDescription: "Get help to improve clarity and engagement of your post.",
    versions: "Versions",
    settings: "Settings",
    settingsDescription: "Configure your post.",
    translations: "Translations",
    alsoIn: "Also in",
    translationsDescription: "Translate your post automatically to other languages. Reach more readers.",
    share: "Share",
    shareDescription: "Get a link to share your post on social media.",
    focusMode: "Focus mode",
    focusModeDescription: "Turn the editor into a distraction-free mode, so you can focus on your writing.",
    translationOutdated: "This translation was made for a previous version",
    mainLanguage: "Main language",
    alternativeLanguages: "Alternative languages",
    alternativeLanguagesDescription: "Enable translations for your post. Toggle languages on to generate them, or off to remove them.",
    translationsSaved: "Translations saved successfully",
    translationOutdatedMessage: "This translation is outdated.",
    translationUpToDate: "This translation is up to date.",
    regenerateTranslation: "Update it",
    translationOutdatedOr: "Or",
    markTranslationAsUpdated: "Mark as updated",
    updatingTranslation: "Updating translation...",
    translationRegenerated: "Translation updated successfully",
    saveChanges: "Save changes",
    saveAndGenerate: "Save and generate",
    saveAndDelete: "Save and delete",
    publishPage: {
      update: "Update",
      publish: "Publish",
      delivery: "Delivery",
      deliveryDescription: "How do you want to deliver this post?",
      sendViaEmail: "Send via email",
      alreadySent: "You've already sent this post to your email subscribers on {date}",
      subscribersWillReceive: "{{No subscribers|1 subscriber|?? subscribers}} will receive this post in their inbox.",
      sendTestToMyself: "Send test to myself",
      testEmailSent: "Test email sent successfully",
      testEmailError: "Failed to send test email",
      audienceTitle: "Audience",
      audienceDescription: "Choose who can view this post.",
    },
  },
  editor: {
    defaultPlaceholder: "Write something …",
    highlightColor: "Highlight color",
    noHighlight: "No highlight",
    grey: "Grey",
    yellow: "Yellow",
    blue: "Blue",
    green: "Green",
    orange: "Orange",
    red: "Red",
    bulletList: "Bullet list",
    orderedList: "Ordered list",
    image: "Image",
    code: "Code",
    quote: "Quote",
    subscribe: "Subscribe",
    imageTitlePlaceholder: "Image title",
    imageDescriptionPlaceholder: "Description for accessibility",
    imageSizeSmall: "Small",
    imageSizeMedium: "Medium",
    imageSizeBig: "Full width",
    turnIntoLabel: "Turn into...",
    heading1: "Heading 1",
    heading2: "Heading 2",
    heading3: "Heading 3",
    blockquote: "Blockquote",
    text: "Text",
    callout: "Callout",
    bold: "Bold",
    italic: "Italic",
    strikethrough: "Strikethrough",
    inlineCode: "Inline code",
    highlight: "Highlight",
    link: "Link",
    horizontalRule: "Divider",
    undo: "Undo",
    redo: "Redo",
    insertBlock: "Insert block",
    more: "More",
    snippet: "Snippet",
    codeBlock: "Code Block",
    math: "Math",
    embed: "Embed",
    embedDisplay: "Display",
    embedDisplayEmbed: "Interactive",
    embedDisplayCard: "Card",
    language: "Language",
  },
  notes: {
    newNote: "New note",
    cancel: "cancel",
    noPinnedYet: "no notes yet",
    voice: "voice",
    copyToPost: "copy to post",
  },
  blog: {
    title: "Publication",
    audience: "Audience",
    forms: "Forms",
    stats: "Stats",
    noResultsFound: "No results found",
    noPostsYet: "You haven't created any posts yet.",
    filter: {
      spam: "Spam",
      archived: "Archived",
      sent: "Sent",
      published: "Published",
      draft: "Draft",
    },
    blogName: "Publication name",
    blogNameDescription: "Set your publication name: it will be used to identify your publication and to generate the url",
    atLeast3Characters: "Use 3-15 characters: letters, numbers, or hyphens.",
    nameAvailable: "This name is available.",
    nameInvalid: "This name is invalid.",
    nameUnavailable: "This name is already taken.",
    nameNaughty: "This name is not allowed.",
    nameChangeCooldown: "You can change your name again in {days:number} {{day|days}}.",
    nameChangeWarning: "After changing, you won't be able to change your name for 30 days. Your old address will redirect to the new one.",
    checkingNameAvailability: "Checking name availability...",
    language: "Language",
    languageDescription: "Set your publication language",
    additionalLanguages: "Additional languages",
    additionalLanguagesDescription: "Choose which languages are available for translations in your publication. All languages are enabled by default.",
    titleAndDescription: "Title and description",
    titleAndDescriptionDescription: "Set your space title and description",
    yourBlogTitle: "your-publication-title",
    description: "Description",
    yourBlogDescription: "your-publication-description",
    branding: "Branding",
    brandingDescription: "Add your own branding to your publication and emails",
    logo: "Logo",
    logoDescription: "This will be shown in the top of your publication, and in the emails. Recommended size: 200 x 200 pixels.",
    uploadLogo: "Upload logo",
    wordmark: "Wordmark",
    wordmarkDescription: "This will be shown instead of your publication name in the top of your publication. Recommended size: 1000 x 200 pixels.",
    uploadWordmark: "Upload wordmark",
    subscribeImage: "Subscribe image",
    subscribeImageDescription: "This will be shown in the subscribe form. recommended size: 600 x 600 pixels.",
    uploadSubscribeImage: "Upload subscribe image",
    favicon: "Favicon",
    faviconDescription: "This will be shown as the browser tab icon for your publication. Recommended size: 128 x 128 pixels.",
    uploadFavicon: "Upload favicon",
    header: "Header",
    headerDescription: "Add a personalized header to each one of your posts",
    footer: "Footer",
    footerDescription: "Add a personalized footer to each one of your posts",
    welcomeEmail: {
      title: "Welcome email",
      description: "Customize the welcome email sent to new subscribers",
      subjectLabel: "Email subject",
      subjectContent: "Email content",
    },
    export: {
      title: "Export",
      description: "Export all the subscribers and the content in your publication",
      generateExport: "Generate Export",
      generating: "Generating...",
      exportedAt: "Exported at",
      subscribers: "Subscribers",
      posts: "Posts",
      download: "Download",
      processing: "Processing...",
      error: "Error",
      noExports: "No exports yet",
    },
    subscribersPage: {
      email: "Email",
      subscribedAt: "Subscribed at",
      tier: "Tier",
      form: "Form",
      allForms: "All forms",
      direct: "Direct",
      free: "Free",
      allTiers: "All tiers",
      noSubscribersFound: "No subscribers found",
      noSubscribersYet: "No subscribers yet",
      publication: "Publication",
      followBack: "Subscribe",
      following: "Subscribed",
    },
    formsPage: {
      editorTitle: "Create form",
      editEditorTitle: "Edit form",
      createForm: "Create form",
      editForm: "Edit form",
      name: "Name",
      audience: "Recipients",
      audienceDescription: "Choose who can receive the email with the files. If access is restricted, people will need to subscribe or upgrade first.",
      downloads: "Files",
      subscribers: "Subscribers",
      size: "Size",
      actions: "Actions",
      everyone: "Everyone",
      everyoneDescription: "Anyone can submit the form and receive the email with the files.",
      subscribersOnly: "Only subscribers",
      subscribersOnlyDescription: "Only subscribers can receive the email with the files.",
      content: "Description",
      email: "Email",
      emailSubject: "Email subject",
      emailBody: "Email body",
      formSectionTitle: "Create form",
      formSectionDescription: "Set up your form and file delivery",
      filesSectionTitle: "Files",
      filesSectionDescription: "Upload the files readers will receive",
      emailSectionDescription: "Customize the delivery email sent after submit",
      defaultDescription: "Subscribe to receive these files by email.",
      defaultEmailSubject: "Your files are ready",
      defaultEmailSubjectWithName: "{publicationName}: your files are ready",
      defaultEmailGreeting: "Hi,",
      defaultEmailIntro: "thanks for subscribing. Your download links are below.",
      defaultEmailOutro: "If you have any questions, just reply to this email.",
      defaultEmailSignature: "- The team",
      defaultEmailSignatureWithName: "- {publicationName}",
      dropFiles: "Drop the files",
      formCreated: "Form created",
      formUpdated: "Form updated",
      formDeleted: "Form deleted",
      blankSlate: {
        title: "Capture, nurture, convert",
        growReadership: "Grow your readership",
        growReadershipDescription: "Capture more readers by offering exclusive content behind a subscription form.",
        convertReaders: "Convert readers into paid subscribers",
        convertReadersDescription: "Offer useful downloads and resources that turn readers into subscribers.",
        goBeyondText: "Go beyond text",
        goBeyondTextDescription: "Give your audience more by supplementing your written content with downloadable media and resources.",
      },
    },
    reactionEmojis: "Reaction emojis",
    reactionEmojisDescription: "Choose which emojis readers can use to react to your posts",
    addEmoji: "Add emoji",
    interval: "interval",
    referrer: "referrer",
    visitors: "visitors",
    views: "views",
    country: "country",
    channel: "channel",
  },
  categories: {
    culture: "Culture",
    technology: "Technology",
    business: "Business",
    usPolitics: "US Politics",
    finance: "Finance",
    food: "Food",
    podcast: "Podcast",
    sports: "Sports",
    art: "Art",
    worldPolitics: "World Politics",
    healthPolitics: "Health Politics",
    news: "News",
    fashionBeauty: "Fashion & Beauty",
    music: "Music",
    faith: "Faith",
    climate: "Climate",
    science: "Science",
    literature: "Literature",
    fiction: "Fiction",
    health: "Health",
    design: "Design",
    travel: "Travel",
    parenting: "Parenting",
    philosophy: "Philosophy",
    comics: "Comics",
    international: "International",
    crypto: "Crypto",
    history: "History",
    humor: "Humor",
    education: "Education",
    filmTv: "Film & TV",
  },
  readerOnboarding: {
    subscribedTo: "You have been subscribed to \"{publicationTitle}\"",
    selectInterests: "What topics interest you?",
    selectInterestsDescription: "Select the categories you'd like to read about. This helps us recommend publications you'll love.",
    continue: "Continue",
    skip: "Skip for now",
  },
  onboarding: {
    buildAudience: "Build your audience on your terms",
    ownYourContent: "On Fika you own what you write and the community that gathers around it.",
    nameYourBlog: "Begin by naming your publication; your own corner on the wild open web.",
    yourBlogNamePlaceholder: "your-publication-name",
    createBlog: "Create publication",
    continue: "Continue",
    updateBlog: "Update publication",
    onboardingRabbitAlt: "Onboarding rabbit",
    chooseLanguage: {
      title: "What languages will you write in?",
      description: "Choose your publication language and enable additional ones for translations.",
    },
    createFirstPost: {
      title: "Publish your first post",
      description: "Start writing your first post and get started with Fika. Share your thoughts with the world!",
      descriptionWithDraft: "Continue writing your first post and get started with Fika. Share your thoughts with the world!",
      ignore: "Ignore",
      createPost: "Create Post",
      continueWriting: "Continue Writing",
    },
    createSecondPost: {
      title: "Publish your second post",
      description: "Write another post and share it with your readers.",
      descriptionWithDraft: "Continue writing your second post.",
      ignore: "Ignore",
      createPost: "Create Post",
      continueWriting: "Continue Writing",
      creating: "Creating...",
    },
    fillProfile: {
      title: "Complete your profile",
      description: "Add your name, bio, and profile image to personalize your Fika presence.",
      ignore: "Ignore",
      editProfile: "Edit Profile",
    },
    setLanguage: {
      title: "Set your publication language",
      description: "Choose the main language for your publication. This helps readers find your content.",
      ignore: "Ignore",
      chooseLanguage: "Choose Language",
    },
    fillPublication: {
      title: "Add publication details",
      description: "Give your publication a title, description, and logo to make it stand out.",
      ignore: "Ignore",
      editDetails: "Edit Details",
    },
    translateArticles: {
      title: "Translate \"{title}\"",
      description: "Add translations to your posts to reach readers in different languages.",
      ignore: "Ignore",
      translateLatestArticle: "Translate article",
    },
  },
  postSettings: {
    title: "settings",
    customPagesTitle: "custom pages",
    customPagesDescription: "make this post appear on the navigation bar",
    customPageLabel: "custom page",
    seoMetadataTitle: "seo metadata",
    seoMetadataDescription: "the title and description of the post as it will appear in search results",
    seoMetadataSaved: "metadata saved",
    metaTitleLabel: "meta title",
    metaTitlePlaceholder: "your publication title",
    metaDescriptionLabel: "meta description",
    metaDescriptionPlaceholder: "your publication description",
    canonicalTitle: "canonical",
    canonicalDescription: "if the original source of the post is another website, you can specify the canonical URL here to prevent duplicate content issues.",
    canonicalUrlLabel: "canonical url",
    canonicalUrlSaved: "canonical url saved",
    pinSaved: "custom page saved",
    tagsTitle: "tags",
    tagsDescription: "add tags to your post",
    audienceSaved: "audience settings saved",
    audienceTitle: "Audience",
    audienceDescription: "Choose who can access this post.",
    audienceEveryoneLabel: "Everyone",
    audienceEveryoneDescription: "Anyone can view, including visitors who aren't subscribed.",
    audienceAllSubscribersLabel: "Only subscribers",
    audienceAllSubscribersDescription: "Any subscriber can view this post.",
    commentsEnabledTitle: "Comments",
    commentsEnabledDescription: "Allow readers to comment on this post.",
    publishedAtTitle: "publication date",
    publishedAtDescription: "change the publication date of this post.",
    publishedAtLabel: "publication date",
    publishedAtSaved: "publication date saved",
    archivePostTitle: "archive post",
    unarchivePostTitle: "unarchive post",
    archivePostDescription: "hide this post from your publication.",
    unarchivePostDescription: "show this post on your publication.",
    archivePostSaved: "post archived",
    deletePostTitle: "delete post",
    deletePostDescription: "remove this post entirely.",
    deletePostWarning: "this action cannot be undone.",
    deletePostSaved: "post deleted",
    holdToDelete: "Hold to delete",
    deleting: "Deleting...",
    deleted: "Deleted!",
  },
  postShare: {
    preview: "Preview",
    previewDescription: "How your post will appear on social media and search results",
    articleLink: "Article link",
    copyLink: "Copy link",
    linkCopied: "Link copied to clipboard",
    metaTitleLabel: "Meta title",
    metaTitlePlaceholder: "Your publication title",
    metaDescriptionLabel: "Meta description",
    metaDescriptionPlaceholder: "Your publication description",
    shareOnSocialMedia: "Share on social media",
    shareOn: "Share on {network}",
    publishedCongrats: "Your post is live!",
    shareNow: "Now share it with the world! 🚀",
    backToPost: "Back to the post",
    setCoverImage: "Upload",
    resetCoverImage: "Reset to original",
    coverImageUpdated: "Cover image updated",
    socialImages: "Social images",
    socialImagesDescription: "Download branded images for social media",
    download: "Download",
  },
  postVersions: {
    title: "versions",
    loading: "loading versions...",
    failed: "failed to load versions",
    failedVersion: "failed to load version",
    restore: "restore",
    restored: "version restored successfully",
    language: "language",
    pills: {
      current: "current",
      archived: "archived",
      sent: "sent",
      published: "published",
    },
  },
  subscribe: {
    defaultText: "Subscribe to {userName} to get updates straight to your inbox",
    button: "Subscribe",
    emailPlaceholder: "Enter your email",
  },
  snippet: {
    placeholder: "Describe what you want to create...",
    placeholderUpdate: "Describe what you want to change...",
    buttonCreate: "create",
    buttonUpdate: "update",
    buttonCreating: "Creating...",
    buttonUpdating: "Updating...",
  },
  auth: {
    signInOrSignUp: "Sign in",
    registrationsClosed: "New registrations are closed. Existing users can still sign in.",
    accountSuspendedTitle: "Account suspended",
    accountSuspendedMessage: "Your account has been suspended. Contact humans@fika.bar if you think this is a mistake.",
    continueWithGoogle: "Continue with Google",
    continueWithLinkedIn: "Continue with LinkedIn",
    signingIn: "Signing in...",
    or: "or",
    enterYourEmail: "Enter your email",
    failedToSend: "Failed to send verification code. please try again.",
    sending: "Sending...",
    enterCode: "Please enter the 6-digit code that was sent to",
    verifying: "Verifying...",
    invalidCode: "Invalid or expired code. please try again.",
    useDifferentEmail: "Use a different email",
    subscribeTo: "Subscribe to",
    thisBlog: "this publication",
    getUpdates: "Subscribe to get updates delivered to your inbox",
    subscribe: "Subscribe",
    somethingWentWrong: "something went wrong",
    disclaimerPrefix: "By submitting, you are accepting the",
    disclaimerPrivacy: "privacy policy",
    disclaimerAnd: "and agreeing to the",
    disclaimerTerms: "terms of service",
    tierSelectionTitleSubscribed: "You're subscribed to {publicationName}!",
    tierSelectionTitleSubscribedNoName: "You're subscribed!",
    subscriptionError: "An error occurred. Please try again.",
    verifyEmailSuccess: "Email verified successfully!",
    emailNotVerified: "Please verify your email to perform this action",
    subscriptionConfirmationDescriptionBeforeAuthor: "You'll receive updates from ",
    subscriptionConfirmationDescriptionAfterAuthor: " directly in your inbox.",
    subscriptionConfirmationDescriptionNoAuthor: "You'll receive updates directly in your inbox.",
  },
  unsubscribe: {
    writerSuccess: "you've been unsubscribed",
    writerSuccessDescription: "you won't receive emails from this writer anymore. you can always resubscribe from their profile.",
    marketingSuccess: "you've been unsubscribed",
    marketingSuccessDescription: "you won't receive marketing emails from fika anymore. you can manage your preferences in settings.",
    weeklyFeaturedSuccess: "you've been unsubscribed",
    weeklyFeaturedSuccessDescription: "you won't receive the weekly featured posts email anymore. you can re-enable it in settings.",
    error: "something went wrong",
    invalidToken: "this unsubscribe link is invalid or has expired. please try again from a recent email.",
    missingToken: "no unsubscribe token was provided. please use the link from your email.",
    genericError: "an unexpected error occurred. please try again later.",
    invalidRequest: "invalid request",
    invalidRequestDescription: "this page requires a valid unsubscribe link from an email.",
    signInToManage: "sign in to manage subscriptions",
  },
  subscriptions: {
    title: "Subscriptions",
    manage: "Manage",
    manageTitle: "Manage subscriptions",
    viewOriginal: "View original",
    noPublishedPostsYet: "No published posts yet",
    previousPost: "Previous post",
    nextPost: "Next post",
    following: "Following",
    subscribed: "Subscribed",
    renewsAt: "Renews {date}",
    upgradePlan: "Upgrade",
    tierChangeError: "Failed to change plan",
    manageBilling: "Billing & invoices",
    subscribersOnly: "Subscribers only",
    tableOfContents: "Table of contents",
  },
  discover: {
    readMore: "Read more",
    upgradeToRead: "Upgrade to read",
    humanContent: "Human",
    humanContentTitle: "Human content",
    humanContentDescription: "Fika is highly confident that this content was written by a human with little or no AI assistance.",
  },
  privatePost: {
    subscribeCta: "Subscribe",
    upgradeCta: "Upgrade to unlock",
  },
  applicationLayout: {
    loadingPhrases: {
      synchronizing: "synchronizing...",
      hadronCollider: "putting together the hadron collider...",
      reticulatingSplines: "reticulating splines...",
      wakingHamsters: "waking up the hamsters...",
      consultingOracle: "consulting the oracle...",
      aligningStars: "aligning the stars...",
      fluxCapacitor: "calibrating the flux capacitor...",
      traveling88mph: "traveling at 88 mph...",
      whiteRabbit: "following the white rabbit...",
      reversingPolarity: "reversing the polarity...",
      enhancingImage: "enhancing the image...",
      enteringMatrix: "entering the matrix...",
      findingRing: "finding the one ring...",
      dividingByZero: "dividing by zero...",
    },
  },
  proofreader: {
    title: "Proofreader",
    editorTitle: "Editor",
    editorDescription: "Proofread your post to improve clarity and engagement.",
    principles: {
      grammar: {
        label: "Grammar",
        description: "Fix spelling and grammatical errors.",
        action: "Fix grammar",
      },
      tighten: {
        label: "Tighten",
        description: "Make the text more concise.",
        action: "Tighten up",
      },
      refine: {
        label: "Refine",
        description: "Improve the text clarity, flow and impact.",
        action: "Refine text",
      },
    },
    noFixesFound: "No fixes found",
    countFixes: "{{No fixes|1 fix|?? fixes}}",
    countFixesFound: "{{No fixes|1 fix|?? fixes}} found",
    scanning: "Scanning...",
    percentComplete: "{percent}% complete",
    discard: "Discard",
    acceptAll: "Accept all",
    lenses: {
      title: "Lenses",
      description: "Visualize sentence structure to improve rhythm and readability.",
      off: "off",
      difficulty: "difficulty",
      difficultyDescription: "Shows how difficult your sentences are. Harder sentences appear in warmer colors. To improve, use shorter sentences and simpler words.",
      variation: "variation",
      variationDescription: "Shows how varied your sentence lengths are. Monotonous sentences appear in warmer colors. To improve, use a mix of short and long sentences.",
      legend: {
        difficulty: {
          orange: "Very hard to read",
          amber: "Hard to read",
        },
        variation: {
          orange: "Very monotonous",
          amber: "Monotonous",
        },
      },
      tips: {
        veryHard: "This sentence is very hard to read. Try breaking it into shorter sentences or using simpler words.",
        hard: "This sentence is hard to read. Consider simplifying it.",
        veryMonotonous: "Your sentences are very similar in length. Vary your rhythm by mixing short and long sentences.",
        monotonous: "This sentence is similar to nearby ones. Try varying the length for better flow.",
      },
    },
  },
  inbox: {
    title: "Inbox",
    message: "Message",
    empty: "No letters or comments yet",
    emptyLetters: "No letters yet",
    emptyComments: "No comments yet",
    emptyDescription: "Use this space to see letters from readers and conversations with authors you're subscribed to.",
    unread: "Unread",
    read: "Read",
    inReplyTo: "In reply to:",
    writeMessage: "Write a message...",
    submit: "Submit",
    publishing: "Publishing...",
    you: "You",
    tabAll: "All",
    tabEmails: "Letters",
    tabComments: "Comments",
    typePrivateEmail: "Private letter",
    typePublicComment: "Public comment",
    andMore: "and {count:number} more",
  },
  comments: {
    title: "Comments",
    writeComment: "Write a comment...",
    submit: "Post comment",
    publishing: "Publishing...",
    reply: "Reply",
    delete: "Delete",
    deleting: "Deleting...",
    deleteConfirm: "Delete this comment?",
    empty: "No comments yet. Be the first to comment!",
    anonymousParticipant: "A subscriber",
    flaggedMessage: "Your message was flagged as spam or inappropriate content and couldn't be sent.",
    author: "Author",
    privateVisibility: "Only visible to you and the participants",
    sendAsPrivate: "Send as private letter",
    translateTo: "Translate to {locale:string}",
    showOriginal: "Show original ({locale:string})",
  },
} satisfies BaseTranslation

export default en
